/**
 * Blocos de "o que pede atenção" do layout A + C.
 *
 * `<KpiStrip label items />` — faixa de indicadores clicáveis: `{ id, label, value, foot?, tone?, to?, onClick?,
 * active? }[]`. `tone` colore o número ('crit' | 'warn' | 'ok' | 'pred'); `to` vira link, `onClick` vira botão
 * (filtro da tela, com `aria-pressed` = `active`). Quebra sozinha em telas estreitas.
 *
 * `<PredictionStrip title items />` — faixa âmbar das falhas previstas ("3 falhas previstas nas próximas 24 h"):
 * `{ id, when, text, action?: { label, to?, onClick? } }[]`; `when` é o prazo curto ("~6 h"), em mono. Some sem itens.
 *
 * `<AttentionList title sub link items empty />` — lista "Atenção agora": `{ id, tag, tone, title, where?, action? }`.
 * `tone`: 'pred' (prevista, âmbar), 'crit', 'warn', 'info' ou 'ok'; a etiqueta leva o texto curto (`tag`) e a cor.
 * `link`: `{ label, to }` no canto do título ("Ver fila").
 *
 * `<PressureMap label groups metrics metric onMetricChange selectedId onSelect unit />` — mapa de pressão: um quadrado
 * por item, agrupado (namespace, local…), colorido pela faixa do valor da métrica escolhida:
 * - `metrics`: `{ id, label, bands, format? }[]`; `bands`: faixas em ordem crescente `{ upTo?, tone, label }`
 *   (a última sem `upTo`); `tone` 'low' | 'ok' | 'warn' | 'crit'. `PERCENT_BANDS` traz as do protótipo (60/80/90 %);
 * - `groups`: `{ id, label, tiles: { id, label, values: { [metricId]: number | null } }[] }[]`;
 * - cada quadrado é um botão com `aria-label` "nome: métrica valor (faixa)"; clique ou Enter seleciona
 *   (`aria-pressed`); setas andam entre os quadrados (só um entra na ordem do Tab);
 * - o seletor "Cor por" aparece com mais de uma métrica; a legenda sai das faixas.
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from './layout';

// ---------------------------------------------------------------- indicadores

export type KpiTone = 'crit' | 'warn' | 'ok' | 'pred';

export interface KpiStripItem {
  id: string;
  label: ReactNode;
  value: ReactNode;
  foot?: ReactNode;
  tone?: KpiTone;
  to?: string;
  onClick?: () => void;
  active?: boolean;
  /** Nome acessível do cartão (padrão: rótulo + valor, quando são texto). */
  ariaLabel?: string;
}

export function KpiStrip({ items, label, className }: { items: KpiStripItem[]; label: string; className?: string }) {
  return (
    <ul className={cx('kpi-strip-ac', className)} aria-label={label}>
      {items.map((k) => {
        const body = (
          <>
            <span className="ks-label">{k.label}</span>
            <span className={cx('ks-value', k.tone && `tone-${k.tone}`)}>{k.value}</span>
            {k.foot != null && k.foot !== '' && <span className="ks-foot">{k.foot}</span>}
          </>
        );
        const cls = cx('ks-card', (k.to || k.onClick) && 'ks-link', k.active && 'on');
        return (
          <li key={k.id}>
            {k.to ? (
              <Link to={k.to} className={cls} aria-label={k.ariaLabel}>
                {body}
              </Link>
            ) : k.onClick ? (
              <button type="button" className={cls} aria-pressed={!!k.active} aria-label={k.ariaLabel} onClick={k.onClick}>
                {body}
              </button>
            ) : (
              <div className={cls} aria-label={k.ariaLabel}>
                {body}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------- ações

export interface InsightAction {
  label: ReactNode;
  to?: string;
  onClick?: () => void;
  /** Ação principal (preenchida) em vez da contornada. */
  primary?: boolean;
  danger?: boolean;
}

function ActionButton({ a, className }: { a: InsightAction; className: string }) {
  const cls = cx('btn btn-sm', className, a.primary && 'btn-primary', a.danger && 'btn-danger');
  return a.to ? (
    <Link to={a.to} className={cls} onClick={a.onClick}>
      {a.label}
    </Link>
  ) : (
    <button type="button" className={cls} onClick={a.onClick}>
      {a.label}
    </button>
  );
}

// ---------------------------------------------------------------- falhas previstas

export interface PredictionItem {
  id: string;
  /** Prazo curto: "~6 h", "~2 d". */
  when: ReactNode;
  text: ReactNode;
  action?: InsightAction;
}

export function PredictionStrip({ title, items, className }: { title: ReactNode; items: PredictionItem[]; className?: string }) {
  const id = useId();
  if (!items.length) return null;
  return (
    <section className={cx('pred-strip', className)} aria-labelledby={id}>
      <h2 className="pred-title" id={id}>
        {title}
      </h2>
      <ul className="pred-list">
        {items.map((p) => (
          <li key={p.id} className="pred-row">
            <span className="pred-when">{p.when}</span>
            <span className="pred-text">{p.text}</span>
            {p.action && <ActionButton a={p.action} className="pred-act" />}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------- atenção agora

export type AttentionTone = 'pred' | 'crit' | 'warn' | 'info' | 'ok';

export interface AttentionItem {
  id: string;
  tag: ReactNode;
  tone: AttentionTone;
  title: ReactNode;
  where?: ReactNode;
  /** A linha inteira leva a uma rota (além do botão de ação). */
  to?: string;
  action?: InsightAction;
}

export function AttentionList({
  title,
  sub,
  link,
  items,
  empty,
  className,
}: {
  title: ReactNode;
  sub?: ReactNode;
  link?: { label: ReactNode; to: string };
  items: AttentionItem[];
  empty?: ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <section className={cx('attn', className)} aria-labelledby={id}>
      <div className="attn-head">
        <h2 id={id}>{title}</h2>
        {sub && <span className="attn-sub">{sub}</span>}
        {link && (
          <Link to={link.to} className="attn-link">
            {link.label}
          </Link>
        )}
      </div>
      {items.length ? (
        <ul className="attn-list">
          {items.map((x) => (
            <li key={x.id} className="attn-row">
              <span className={`attn-tag tone-${x.tone}`}>{x.tag}</span>
              <span className="attn-text">
                {x.to ? (
                  <Link to={x.to} className="attn-title">
                    {x.title}
                  </Link>
                ) : (
                  <b className="attn-title">{x.title}</b>
                )}
                {x.where && <span className="attn-where"> · {x.where}</span>}
              </span>
              {x.action && <ActionButton a={x.action} className="attn-act" />}
            </li>
          ))}
        </ul>
      ) : (
        <p className="attn-empty">{empty}</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- mapa de pressão

export type PressureTone = 'low' | 'ok' | 'warn' | 'crit';

export interface PressureBand {
  /** Limite superior (exclusivo) da faixa; a última faixa não tem. */
  upTo?: number;
  tone: PressureTone;
  label: string;
}

export interface PressureMetric {
  id: string;
  label: string;
  bands: PressureBand[];
  /** Como mostrar o valor (padrão: número + "%"). */
  format?: (v: number) => string;
}

export interface PressureTile {
  id: string;
  label: string;
  values: Record<string, number | null | undefined>;
}

export interface PressureGroup {
  id: string;
  label: ReactNode;
  /** Texto ao lado do nome do grupo (padrão: a quantidade de itens). */
  note?: ReactNode;
  tiles: PressureTile[];
}

/** Faixas do protótipo para métricas em %: até 60, 60–80, 80–90, 90+. */
export const PERCENT_BANDS: PressureBand[] = [
  { upTo: 60, tone: 'low', label: '< 60%' },
  { upTo: 80, tone: 'ok', label: '60–80%' },
  { upTo: 90, tone: 'warn', label: '80–90%' },
  { tone: 'crit', label: '90%+' },
];

export function bandOf(bands: PressureBand[], v: number | null | undefined): PressureBand | null {
  if (v == null || Number.isNaN(v)) return null;
  return bands.find((b) => b.upTo == null || v < b.upTo) ?? bands[bands.length - 1] ?? null;
}

export function PressureMap({
  label,
  groups,
  metrics,
  metric: metricProp,
  onMetricChange,
  selectedId,
  onSelect,
  toolbar,
  className,
}: {
  label: string;
  groups: PressureGroup[];
  metrics: PressureMetric[];
  /** Métrica escolhida (controlada); sem ela, a primeira e o seletor guarda a escolha. */
  metric?: string;
  onMetricChange?: (id: string) => void;
  selectedId?: string | null;
  onSelect?: (tile: PressureTile, groupId: string) => void;
  /** Algo a mais na barra do mapa, antes do seletor (ex.: `ViewToggle`). */
  toolbar?: ReactNode;
  className?: string;
}) {
  const { t, fmt } = useI18n<CommonKey>();
  const [own, setOwn] = useState(metrics[0]?.id ?? '');
  const mid = metricProp ?? own;
  const m = metrics.find((x) => x.id === mid) ?? metrics[0];
  const selId = useId();
  const flat = useMemo(() => groups.flatMap((g) => g.tiles.map((tile) => ({ tile, g: g.id }))), [groups]);
  const [focusId, setFocusId] = useState<string | null>(null);
  // só um quadrado na ordem do Tab: o escolhido, o último focado ou o primeiro
  const tabId = flat.some((x) => x.tile.id === focusId) ? focusId : flat.some((x) => x.tile.id === selectedId) ? selectedId : flat[0]?.tile.id;
  const box = useRef<HTMLDivElement>(null);
  const fmtV = (v: number) => (m?.format ? m.format(v) : `${fmt.number(Math.round(v))}%`);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: -Infinity, End: Infinity };
    const d = keys[e.key];
    if (d == null) return;
    e.preventDefault();
    const j = d === -Infinity ? 0 : d === Infinity ? flat.length - 1 : Math.max(0, Math.min(flat.length - 1, i + d));
    const next = flat[j];
    if (!next) return;
    setFocusId(next.tile.id);
    [...(box.current?.querySelectorAll<HTMLButtonElement>('[data-tile]') ?? [])].find((el) => el.dataset.tile === next.tile.id)?.focus();
  };
  let n = -1;
  return (
    <section className={cx('pmap', className)} aria-label={label}>
      <div className="pmap-bar">
        {toolbar}
        {metrics.length > 1 && (
          <label className="pmap-metric" htmlFor={selId}>
            {t('ui.pressure.colorBy')}
            <select
              id={selId}
              value={m?.id}
              onChange={(e) => {
                setOwn(e.target.value);
                onMetricChange?.(e.target.value);
              }}
            >
              {metrics.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <ul className="pmap-legend" aria-label={t('ui.pressure.legend')}>
          {(m?.bands ?? []).map((b) => (
            <li key={b.label}>
              <span className={`pm-sw pm-${b.tone}`} aria-hidden="true" />
              {b.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="pmap-groups" ref={box}>
        {groups.map((g) => (
          <div className="pmap-group" key={g.id}>
            <div className="pmap-ghead">
              <b>{g.label}</b>
              <span className="pmap-note">{g.note ?? fmt.number(g.tiles.length)}</span>
            </div>
            <div className="pmap-tiles">
              {g.tiles.map((tile) => {
                n++;
                const i = n;
                const v = m ? tile.values[m.id] : null;
                const b = m ? bandOf(m.bands, v) : null;
                const desc = v == null || !m ? `${tile.label}: ${m?.label ?? ''} ${t('ui.pressure.noData')}` : `${tile.label}: ${m.label} ${fmtV(v)} (${b?.label ?? ''})`;
                const sel = selectedId === tile.id;
                return (
                  <button
                    key={tile.id}
                    type="button"
                    data-tile={tile.id}
                    className={cx('pm-tile', b ? `pm-${b.tone}` : 'pm-none', sel && 'sel')}
                    aria-label={desc}
                    title={desc}
                    aria-pressed={sel}
                    tabIndex={tile.id === tabId ? 0 : -1}
                    onFocus={() => setFocusId(tile.id)}
                    onKeyDown={(e) => onKey(e, i)}
                    onClick={() => onSelect?.(tile, g.id)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
